import { NotificationItemAdapter } from '@makyoapp/makyo-common'

import { useMyNotificationsContext } from '@/context/MyNotificationsContext'

/**
 * Custom hook `useNotificationsTable` for managing the state and interactions of a notifications table.
 *
 * @returns {Object} The state and interaction handlers for the notifications table.
 *
 * @property {boolean} checked - A boolean indicating if all notifications are selected/checked.
 * @property {Object} notifications - An object that represents the notifications to be displayed in the table.
 * @property {boolean} isLoading - A boolean indicating if the notifications are currently being loaded.
 * @property {Object} selectedNotif - The currently selected notification.
 * @property {function} setSelectedNotif - Function to set the currently selected notification.
 * @property {function} handleCheck - Function to handle the check/uncheck all action.
 * @property {function} handleRead - Function to mark a single notification as read.
 *
 */
const useNotificationsTable = () => {
  const { setNotifications, checks, setChecks, selectedNotif, setSelectedNotif, isLoading, notifications } =
    useMyNotificationsContext()

  // Logic to check if all notifications are selected/checked
  const checked = notifications.items.length > 0 && checks.length === notifications.items.length

  // Function to handle the check/uncheck all action
  const handleCheck = () => {
    // If not all notifications are checked, check all
    if (checks.length !== notifications.items.length) {
      setChecks([...notifications.items])
    }
    // If all notifications are checked, uncheck all
    else {
      setChecks([])
    }
  }

  // Function to mark a single notification as read
  const handleRead = (notification: NotificationItemAdapter) => {
    // Set the `isRead` property of the notification to true
    notification.isRead = true

    // Find the index of the notification in the notifications list
    const idx = notifications.items.findIndex((i) => i.id === notification.id)

    // If found, replace the notification at this index with the updated notification
    if (idx !== -1) {
      notifications.items[idx] = notification
    }

    // Set the new notifications state with the updated notification
    setNotifications({
      items: notifications.items,
      totalCount: notifications.totalCount,
    })
  }

  return {
    checked,
    notifications,
    isLoading,
    selectedNotif,
    setSelectedNotif,
    handleCheck,
    handleRead,
  }
}

export default useNotificationsTable
